import React, { Component } from 'react';
import { makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Progress } from 'reactstrap';
import { FaExclamation, FaClock, FaExclamationCircle } from 'react-icons/fa';

import { ISalaryMonthViewModelResponse, SalaryMonthViewModelResponseExt, PersonActivitiesInfo, PersonContractInfo, SalaryPeriodResponseItem } from '@Models';
import { stringSearchService } from '@Services';
import { FilterSectionStore } from '@Pages/Home/Components';
import PersonContextMenu from '../PersonContextMenu';
import {SizeConfig, DataGrid, DataGridColumnDef, CrossTableHoverStore} from '@Components';
import { CompletionType, PromiseCompletion } from '@Helpers';
import { PersonError } from './PersonError';

const ITEM_SIZE = 25;

type PersonTableProps = {
    dataSource: SalaryMonthViewModelResponseExt[];
    fullDataSource?: SalaryMonthViewModelResponseExt[];
    columns?: string[];
    classPreffix?: string;
    selectedRow?: SalaryMonthViewModelResponseExt;
    crossTableHoverStore?: CrossTableHoverStore;
    scrollHandler?: (top: number) => void;
    onRowClick?: (item: SalaryMonthViewModelResponseExt) => void;
    onRowDoubleClick?: (row: SalaryMonthViewModelResponseExt) => void;
    onLoadError: (personId: string) => Promise<string>;
    sizeConfig?: SizeConfig;
    filterStore: FilterSectionStore;
    loading?: boolean;
    timePeriod: SalaryPeriodResponseItem | null;
    personActivitiesInfoMap: Map<string, PersonActivitiesInfo>;
    scrollTop?: number;
    onScroll?: (scrollTop: number) => void;
    headerHeight?: number;
    onHeaderHeightChange?: (height: number) => void;
    totalRowAdditionalHeight?: number;
    onFilteredItemsChange?: (items: SalaryMonthViewModelResponseExt[]) => void;
    onColumnFiltersActiveChange?: (isActive: boolean) => void;
};

@observer
class PersonTable extends Component<PersonTableProps, {}> {
    @observable.ref private _personListRef: React.RefObject<HTMLDivElement> = React.createRef();
    @observable.shallow private _loaders: Map<string, PromiseCompletion> = new Map();
    private _dataGridRef = React.createRef<DataGrid<SalaryMonthViewModelResponseExt>>();

    public scrollTo(top: number) {
        this._dataGridRef.current?.scrollTo(top);
    }

    constructor(props: PersonTableProps) {
        super(props);
        makeObservable(this);
    }

    render() {
        const { dataSource, fullDataSource, sizeConfig, onRowClick, timePeriod, loading, scrollTop, onScroll, headerHeight, onHeaderHeightChange, selectedRow, totalRowAdditionalHeight, onFilteredItemsChange, onColumnFiltersActiveChange } = this.props;
        const maxHeight = sizeConfig?.height != null ? `${sizeConfig.height}px` : undefined;
        return (
            <>
                <div ref={this._personListRef}>
                    <DataGrid<SalaryMonthViewModelResponseExt>
                        ref={this._dataGridRef}
                        tableKey="person-table"
                        idField="personId"
                        columns={this._columns}
                        items={dataSource}
                        initialItemsList={fullDataSource ?? dataSource}
                        filteredItemsWithoutColumnFilters={dataSource}
                        maxHeight={maxHeight}
                        onRowClick={onRowClick}
                        getRowAttributes={(item) => ({ 'data-menu-person-id': item.personId })}
                        renderRowOverlay={this._renderRowProgress}
                        scrollTop={scrollTop}
                        onScroll={onScroll}
                        headerHeight={headerHeight}
                        onHeaderHeightChange={onHeaderHeightChange}
                        selectedRowId={selectedRow?.personId}
                        crossTableHoverStore={this.props.crossTableHoverStore}
                        loading={loading}
                        totalRow={[]}
                        totalRowAdditionalHeight={totalRowAdditionalHeight}
                        onOwnFilteredItemsChange={onFilteredItemsChange}
                        onColumnFiltersActiveChange={onColumnFiltersActiveChange}
                    />
                </div>
                <PersonContextMenu
                    container={this._personListRef}
                    timePeriod={timePeriod}
                    onProgress={this._onPersonProgress}
                />
            </>
        );
    }

    private get _columns(): DataGridColumnDef<SalaryMonthViewModelResponseExt>[] {
        return [
            {
                title: 'Code',
                dataField: 'personCode',
                minWidth: '55px',
                maxWidth: '65px',
                renderCell: (value, item) => this._codeCellRender(value as unknown as string, item)
            },
            {
                title: 'Function',
                dataField: 'personFunctions',
                minWidth: '55px',
                maxWidth: '65px',
                renderCell: (value) => this._functionsCellRender(value as unknown as string[])
            },
            {
                title: 'Contract',
                dataField: 'personContracts',
                minWidth: '55px',
                maxWidth: '65px',
                getFilterItemOptionName: (contract) => (contract as PersonContractInfo).contractType ?? '',
                renderCell: (value) => this._contractsCellRender(value as unknown as PersonContractInfo[])
            },
            {
                title: 'Name',
                dataField: 'personFullName',
                className: 'text',
                minWidth: '130px',
                renderCell: (value) => this._cellRender(value as unknown as string)
            }
        ];
    }

    private _onPersonProgress = async (personId: string, operation: Promise<unknown>) => {
        await this.addOperation(personId, operation);
    };

    public async addOperation(personId: string, operation: PromiseLike<unknown>) {
        const loader = runInAction(() => {
            if (!this._loaders.has(personId)) {
                this._loaders.set(personId, new PromiseCompletion(CompletionType.Pending));
            }
            return this._loaders.get(personId);
        });

        if (!loader) return;

        void loader.subscribe(operation);
        await loader.wait();

        if (!loader.isPending) {
            runInAction(() => this._loaders.delete(personId));
        }
    }

    private _renderRowProgress = (item: SalaryMonthViewModelResponseExt) => {
        if (!this._loaders.get(item.personId)?.isPending) return null;

        return <Progress animated value={100} className="person-progress" />;
    };

    public virtualRowHeight = () => {
        return ITEM_SIZE;
    };

    private _codeCellRender = (value: string, item: SalaryMonthViewModelResponseExt) => {
        const { onLoadError, personActivitiesInfoMap } = this.props;
        const { status, personId } = item as ISalaryMonthViewModelResponse;
        const personActivityInfo = personActivitiesInfoMap.get(personId);

        return (
            <>
                <span>{this._renderText(value, true)}</span>
                {(status === 'None') && <span className="period-status" title="No duties loaded yet"><FaClock /></span>}
                {(status === 'Failed') && <PersonError Icon={FaExclamationCircle} title="Failed to import person duties:" onLoadError={() => onLoadError(personId)}/>}
                {(!personActivityInfo?.hasDuties && status === 'Imported') && <PersonError Icon={FaExclamation} title="No duties loaded for imported period"/>}
            </>
        );
    };

    private _cellRender = (value: string) => {
        return <>{this._renderText(value, true)}</>;
    };

    private _functionsCellRender = (value: string[]) => {
        return (<>
            {value.map((v, index) => <React.Fragment key={index}>
                {index > 0 ? ', ' : ''}
                {this._renderText(v, true)}
            </React.Fragment>)}
        </>);
    };

    private _contractsCellRender = (value: PersonContractInfo[]) => {
        return (<>
            {value.map((v, index) => <React.Fragment key={index}>
                {index > 0 ? ', ' : ''}
                {this._renderText(v.contractType, true)}
            </React.Fragment>)}
        </>);
    };

    private _renderText(text: string | undefined, shouldSearch: boolean) {
        if (!shouldSearch) {
            return text;
        }

        const { searchTerms, textComparer } = this.props.filterStore;

        const parts = stringSearchService.markMatches(text, searchTerms, textComparer);
        return (
            <>
                {parts.map((p, index) => p.isMatch ? <span className="text-match" key={'t' + index}>{p.text}</span> : p.text)}
            </>
        );
    }
}

export { PersonTable };
